import styled, {css} from 'styled-components';
import {
  StyledFC,
  dimensions,
  colors,
  Knob,
  KnobType,
  KnobSize
} from '@bettermarks/bm-ui-components';
import {CheckBox} from '../Checkbox';
import {Notification} from '../Notification';
import {CloseIcon} from '../Icons/Close';
import {AnimatePresence, motion} from 'framer-motion';
import {AssignmentButton, TabNavItem} from '../modal/AllocationModal';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {solid} from '@fortawesome/fontawesome-svg-core/import.macro';
import {Button, Theme, Variant} from '../Button';

interface AllocateToolbarProps {
  state?: 'initial' | 'lobSelected';
  count: number;
  selectedDate?: any;
  selectedStudents: any;
  deselect?: () => void;
  allocate?: () => void;
  createAssignment: () => void;
  disabledButton?: boolean;
}

const Container = styled(motion.div)<{minHeight?: number}>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;

  transition: all 0.25s ease-in-out;
  ${CheckBox} {
    cursor: default;
  }
  min-height: ${p => p.minHeight + 'px' || 'initial'};
`;

const InlineContainer = styled(Container)`
  display: flex;
  flex: 1;
  gap: 12px;
  transition: all 0.25s ease-in-out;
  justify-content: space-between;
  align-items: center;
`;

const RemoveIcon = styled.span`
  flex: 0 0 20px;
  border-radius: 50%;
  background-color: ${colors.cGray700};
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
  oultine: 0;
  cursor: pointer;
  svg {
    width: 10px;
    height: 10px;
  }
`;

const Group = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const _AllocateToolbar: StyledFC<AllocateToolbarProps> = ({
  className,
  count,
  selectedDate,
  selectedStudents,
  deselect,
  allocate,
  createAssignment,
  disabledButton
}) => {
  return (
    <div className={className}>
      {count > 0 ? (
        <Container>
          <Group>
            <Notification>{count}</Notification>
            {count > 1 ? 'Lernziele' : 'Lernziel'}
            <TabNavItem
              icon={<FontAwesomeIcon icon={solid('address-book')} />}
              title={selectedStudents}
              arrow
              activeTab={false}
              setActiveTab={allocate}
            />
            <TabNavItem
              icon={<FontAwesomeIcon icon={solid('calendar-days')} />}
              title={selectedDate}
              arrow
              activeTab={false}
              setActiveTab={allocate}
            />

            <Button
              variant={Variant.solid}
              onClick={!disabledButton ? createAssignment : null}
              disabled={disabledButton}
            >
              als To-Do zuweisen
            </Button>
          </Group>

          <Button
            onClick={deselect}
            buttonTheme={Theme.neutral}
            variant={Variant.ghost}
          >
            <RemoveIcon>
              <CloseIcon fill={colors.cWhite} />
            </RemoveIcon>
            Abbrechen
          </Button>
        </Container>
      ) : (
        <Container>
          Wählen Sie mit <CheckBox /> Lernziele aus, die Sie Ihren Schülern
          zuweisen möchten.
        </Container>
      )}
    </div>
  );
};

const AllocateToolbar = styled(_AllocateToolbar)`
  padding: 0 ${dimensions.spaceM};
  background-color: ${colors.cWhite};
  display: flex;

  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  min-height: 60px;
  z-index: 1;
  box-shadow: ${p =>
    p.count > 0
      ? `inset 0 -1px 0 ${colors.cGray400},
            0px 12px 20px rgba(0, 0, 0, 0.15)`
      : `inset 0 -1px 0 ${colors.cGray400},
            0px 0px 20px rgba(0, 0, 0, 0)`};

  transition: box-shadow 0.25s ease-in-out;
  ${Knob} {
    height: 40px;
  }
`;

export default AllocateToolbar;
